import React, { useState } from 'react';
import { IconButton, InputAdornment, SxProps, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FieldHookConfig, useField } from 'formik';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { setFieldOnSessionStorage } from '../../helpers/helpers';

type TextFieldProps = FieldHookConfig<string> & {
  label: string;
  id: string;
  autoComplete?: string;
  formName?: string;
  objectId?: string;
  required?: boolean;
  invalidPattern?: RegExp;
  smallPadding?: boolean;
  type?: string;
  error?: string;
  clearError?: (errorKey: string) => void;
  helperText?: string;
  styles?: SxProps;
};

const FormikTextField = (props: TextFieldProps) => {
  const [field, meta, helpers] = useField(props);
  const [showPassword, setShowPassword] = useState(false);
  const {
    label,
    id,
    formName,
    objectId = '',
    autoComplete = 'on',
    invalidPattern = null,
    required = false,
    type = 'text',
    smallPadding = false,
    error,
    clearError,
    helperText = '',
    styles,
  } = props;
  const { t } = useTranslation();
  const { setValue } = helpers;

  const changeField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value.replaceAll(invalidPattern, '');
    clearError?.(id);
    if (formName) {
      setFieldOnSessionStorage(formName, objectId, field.name, newValue);
    }
    setValue(newValue);
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  return (
    <TextField
      id={id}
      label={label}
      fullWidth
      autoComplete={autoComplete}
      required={required}
      type={showPassword ? 'text' : type}
      size={smallPadding ? 'small' : 'medium'}
      helperText={
        (Boolean(error) && error) ||
        (meta.touched && meta.error && t(meta.error, { field: label })) ||
        helperText
      }
      {...field}
      onChange={changeField}
      error={Boolean(error) || (meta.touched && Boolean(meta.error))}
      InputProps={{
        endAdornment: (
          <>
            {meta.touched && Boolean(meta.error) && (
              <InputAdornment position="end">
                <WarningAmberIcon color="error" />
              </InputAdornment>
            )}
            {type === 'password' && (
              <InputAdornment position="end">
                <IconButton
                  aria-label={`toggle ${label} visibility`}
                  title={`toggle ${label} visibility`}
                  onClick={() => setShowPassword(!showPassword)}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )}
          </>
        ),
      }}
      sx={{
        marginBottom: { xs: '16px', sm: '24px' },
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: 'grey.23p',
        },
        ...styles,
      }}
    />
  );
};

export default FormikTextField;
